import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { motion, AnimatePresence } from 'framer-motion';

// Custom Quill Toolbar with image handler
const imageHandler = function () {
  const input = document.createElement('input');
  input.setAttribute('type', 'file');
  input.setAttribute('accept', 'image/*');
  input.click();

  input.onchange = () => {
    const file = input.files[0];
    if (file) {
      // Check file size (2MB limit)
      if (file.size > 2 * 1024 * 1024) {
        alert('Image size should be less than 2MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = () => {
        const range = this.quill.getSelection();
        this.quill.insertEmbed(range.index, 'image', reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
};

const quillModules = {
  toolbar: {
    container: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link', 'image'],
      ['clean'],
    ],
    handlers: {
      image: imageHandler,
    },
  },
};

const statusColors = {
  published: '#10b981',
  pending: '#f59e0b',
  rejected: '#ef4444',
  draft: '#64748b'
};

const User = () => {
  const [posts, setPosts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [newPost, setNewPost] = useState({ title: '', content: '' });
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showPostModal, setShowPostModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [imageWarning, setImageWarning] = useState('');
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem('user'));
  const username = user?.username;

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const res = await fetch(`https://api.dikshasea.in/api/posts/user/${user.id}`);
      const data = await res.json();
      setPosts(data || []);
    } catch (error) {
      console.error('Error fetching posts:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const token = user?.token;

    try {
      const res = await fetch('https://api.dikshasea.in/api/posts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ ...newPost, status: 'pending', author_id: user.id })
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.message || 'Post creation failed');
      }

      setNewPost({ title: '', content: '' });
      setShowModal(false);
      alert('Post submitted for approval!');
      fetchPosts();
    } catch (error) {
      alert('Error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const filteredPosts = posts.filter(post =>
    post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.content.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div style={styles.container}>
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={styles.header}
      >
        <h1 style={styles.title}>📋 {username}'s Dashboard</h1>
        <div style={styles.actions}>
          <motion.button 
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={styles.primaryButton} 
            onClick={() => setShowModal(true)}
          >
            ➕ Add Post
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{ ...styles.primaryButton, ...styles.logoutButton }}
            
            onClick={() => {
              localStorage.removeItem('user');
              navigate('/login');
            }}
          >
              <i className="fas fa-sign-out-alt" ></i>
            Logout
          </motion.button>
        </div>
      </motion.div>

      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.5 }}
        style={styles.searchContainer}
      >
        <div style={styles.searchWrapper}>
          <input
            type="text"
            placeholder="Search posts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
          <svg
            style={styles.searchIcon}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </motion.div>

      {filteredPosts.length > 0 ? (
        <motion.div 
          layout
          style={styles.cardGrid}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.5 }}
        >
          <AnimatePresence>
            {filteredPosts.map((post) => (
              <motion.div
                key={post.id}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
                whileHover={{ y: -5, boxShadow: '0 10px 20px rgba(0,0,0,0.1)' }}
                style={{
                  ...styles.card,
                  borderLeft: `4px solid ${statusColors[post.status] || '#4f46e5'}`
                }}
                onClick={() => {
                  setSelectedPost(post);
                  setEditContent(post.content);
                  setShowPostModal(true);
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    backgroundColor: statusColors[post.status] || '#4f46e5',
                    color: 'white',
                    padding: '4px 10px',
                    fontSize: '12px',
                    borderBottomLeftRadius: '10px',
                    borderTopRightRadius: '10px',
                    textTransform: 'capitalize'
                  }}
                >
                  {post.status}
                </div>
                <h3 style={styles.cardTitle}>{post.title}</h3>
                <div style={styles.meta}>
                  <span>🧑 {post.author_name}</span>
                  <span>🕒 {formatDate(post.created_at)}</span>
                </div>
                <div>
                  <span style={styles.readMore}>👁️ View Post</span>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          style={styles.noPosts}
        >
          <p>No posts found. Create your first post!</p>
        </motion.div>
      )}

      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={styles.modalOverlay}
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ y: -50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 50, opacity: 0 }}
              transition={{ type: 'spring', damping: 25 }}
              style={styles.modalContainer} // Changed to modalContainer
              onClick={(e) => e.stopPropagation()}
            >
              <div style={styles.modalContent}> {/* Added modalContent wrapper */}
                <h2>📝 New Post</h2>
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <input
                    type="text"
                    placeholder="Title"
                    value={newPost.title}
                    onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                    style={styles.input}
                    required
                  />
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', margin: '1rem 0' }}>
                    <ReactQuill
                      value={newPost.content}
                      onChange={(value) => setNewPost({ ...newPost, content: value })}
                      modules={quillModules}
                      style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
                      theme="snow"
                    />
                    {imageWarning && (
                      <div style={{ color: 'red', marginTop: '5px', fontSize: '14px' }}>
                        {imageWarning}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', paddingTop: '1rem' }}>
                    <motion.button
                      type="button"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      style={styles.secondaryButton}
                      onClick={() => setShowModal(false)}
                    >
                      Cancel
                    </motion.button>
                    <motion.button
                      type="submit"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      style={styles.primaryButton}
                      disabled={loading}
                    >
                      {loading ? 'Submitting...' : 'Submit'}
                    </motion.button>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* View/Edit Post Modal */}
      <AnimatePresence>
        {showPostModal && selectedPost && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={styles.modalOverlay}
            onClick={() => setShowPostModal(false)}
          >
            <motion.div
              initial={{ y: -50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 50, opacity: 0 }}
              transition={{ type: 'spring', damping: 25 }}
              style={{ ...styles.modalContainer, maxWidth: '800px' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={styles.modalContent}>
                <h2>{selectedPost.title}</h2>
                <p style={{ fontSize: '15px', color: '#64748b', marginBottom: '1rem' }}>
                  🧑 {selectedPost.author_name} | 🕒 {formatDate(selectedPost.created_at)} | 
                  <span style={{ 
                    backgroundColor: statusColors[selectedPost.status] || '#4f46e5',
                    color: 'white',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    marginLeft: '8px',
                    textTransform: 'capitalize'
                  }}>
                    {selectedPost.status}
                  </span>
                </p>
                <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '1rem 0' }} />
                <div style={{ flex: 1, overflowY: 'auto', marginBottom: '1rem' }}>
                  {selectedPost.status === 'pending' ? (
                    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                      <ReactQuill
                        value={editContent}
                        onChange={(value) => setEditContent(value)}
                        modules={quillModules}
                        style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
                        theme="snow"
                      />
                      {imageWarning && (
                        <div style={{ color: 'red', marginTop: '5px', fontSize: '14px' }}>
                          {imageWarning}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={styles.quillContent}>
                      <div dangerouslySetInnerHTML={{ __html: selectedPost.content }} />
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', paddingTop: '1rem' }}>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    style={styles.secondaryButton}
                    onClick={() => setShowPostModal(false)}
                  >
                    Close
                  </motion.button>
                  {selectedPost.status === 'pending' && (
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      style={styles.primaryButton}
                      onClick={async () => {
                        setLoading(true);
                        try {
                          const token = user?.token;
                          const res = await fetch(`https://api.dikshasea.in/api/posts/${selectedPost.id}`, {
                            method: 'PUT',
                            headers: {
                              'Content-Type': 'application/json',
                              Authorization: `Bearer ${token}`
                            },
                            body: JSON.stringify({ ...selectedPost, content: editContent })
                          });
                          if (!res.ok) {
                            const errData = await res.json();
                            throw new Error(errData.message || 'Update failed');
                          }
                          alert('Post updated!');
                          setShowPostModal(false);
                          fetchPosts();
                        } catch (error) {
                          alert('Error: ' + error.message);
                        } finally {
                          setLoading(false);
                        }
                      }}
                      disabled={loading}
                    >
                      {loading ? 'Saving...' : 'Save Changes'}
                    </motion.button>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const styles = {
  container: {
    padding: '20px',
    maxWidth: '1200px',
    margin: '0 auto',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    backgroundColor: '#f8fafc',
    color: '#1e293b',
    minHeight: '100vh',
    boxSizing: 'border-box',
    marginTop: '5rem',
    overflowX: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    padding: '1.5rem',
    borderRadius: '12px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    marginBottom: '1.5rem',
    flexWrap: 'wrap',
    gap: '1rem',
  },
  title: {
    margin: '0',
    fontSize: '1.5rem',
    fontWeight: '600',
    color: '#1e293b',
  },
  actions: {
    display: 'flex',
    gap: '0.75rem',
  },
  primaryButton: {
    backgroundColor: '#4f46e5',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '0.625rem 1.25rem',
    fontSize: '0.875rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  secondaryButton: {
    backgroundColor: '#e2e8f0',
    color: '#475569',
    border: 'none',
    borderRadius: '8px',
    padding: '0.625rem 1.25rem',
    fontSize: '0.875rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  logoutButton: {
    backgroundColor: '#ef4444',
  },
  searchContainer: {
    marginBottom: '1.5rem',
  },
  searchWrapper: {
    position: 'relative',
    maxWidth: '500px',
    margin: '0 auto',
  },
  searchInput: {
    width: '100%',
    padding: '0.75rem 1rem 0.75rem 2.5rem',
    fontSize: '0.875rem',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    backgroundColor: '#ffffff',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    outline: 'none',
    color: '#1e293b',
  },
  searchIcon: {
    position: 'absolute',
    left: '1rem',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '1rem',
    height: '1rem',
    color: '#9ca3af',
  },
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '1.5rem',
  },
  card: {
    position: 'relative',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    padding: '1.5rem',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
    borderLeft: '4px solid #4f46e5',
    wordBreak: 'break-word',
    minHeight: '180px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  cardTitle: {
    margin: '0 0 0.75rem 0',
    fontSize: '1.125rem',
    fontWeight: '600',
    color: '#1e293b',
    paddingRight: '2rem',
  },
  meta: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.75rem',
    color: '#64748b',
    margin: '0.5rem 0',
    flexWrap: 'wrap',
    gap: '0.5rem',
  },
  readMore: {
    color: '#4f46e5',
    fontWeight: '500',
    fontSize: '0.75rem',
    marginTop: '0.5rem',
    display: 'inline-block',
  },
  noPosts: {
    textAlign: 'center',
    padding: '2rem',
    gridColumn: '1 / -1',
    color: '#64748b',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
    modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start', // Changed to flex-start
    zIndex: 1000,
    padding: '2rem 1rem',
    overflowY: 'auto', // Enable scrolling for overlay
    backdropFilter: 'blur(5px)',
  },
  modalContainer: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    width: '100%',
    maxWidth: '600px',
    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    margin: 'auto 0', // Center vertically but allow scrolling
    display: 'flex',
    flexDirection: 'column',
  },
  modalContent: {
    padding: '2rem',
    overflow: 'hidden', // Prevent content from causing double scroll
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  quillContent: {
    minHeight: '200px',
    maxHeight: '50vh', // Limit height and enable scrolling
    padding: '1rem',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
    overflowY: 'auto',
  },
  '.ql-container': {
    minHeight: '200px',
    maxHeight: '40vh', // Limit height for Quill container
    fontSize: '16px',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    overflowY: 'auto',
  },
  '.ql-editor': {
    minHeight: '200px',
    fontSize: '16px',
    lineHeight: '1.7',
    color: '#1e293b',
    padding: '12px',
  
  },
};

export default User;